/**
 * DatasetCompareDialog — Inteligência de Dados (Fase 3 — Comparar)
 * -----------------------------------------------------------------------------
 * Modal de cruzamento entre os registros importados de um sistema externo e as
 * entidades internas do tenant (clientes, contatos e veículos).
 *
 * Como funciona:
 *   - O usuário escolhe o campo do dataset (ex.: "cliente.cpf") e a chave da
 *     entidade interna (ex.: CPF/CNPJ do cliente).
 *   - A RPC `compare_external_dataset` normaliza os dois lados no banco
 *     (documento/placa sem máscara, telefone só dígitos, nome sem acento) e
 *     devolve os registros já cadastrados (MATCH) ou ausentes (NOT_MATCH).
 *
 * Performance:
 *   - Tudo é paginado no servidor; o modal nunca baixa o dataset inteiro.
 *   - Os totais de cada lado vêm na mesma consulta (sem round-trip extra).
 *
 * @version 1.0.0
 * RELEASE: 2026-08-23 v1.0.0 - Criação inicial (Fase 3 - Comparar).
 */

import { useMemo, useState } from 'react';
import { GitCompareArrows, Download, Search, CheckCircle2, XCircle } from 'lucide-react';

import { Dialog, DialogContent } from '@/components/ui/dialog';
import { PremiumDialogHeader } from '@/components/shared/PremiumDialogHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { InlineNotice } from '@/components/ui/inline-notice';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useDatasetComparison,
  COMPARE_KEYS_BY_TARGET,
  type CompareKey,
  type CompareTarget,
} from '@/hooks/data/useDatasetRecords';
import type { ExternalDatasetField } from '@/hooks/data/useExternalDatasets';

/** Página fixa da comparação (server-side) */
const PAGE_SIZE = 50;

/** Rótulos das entidades internas comparáveis */
const TARGET_LABELS: Record<CompareTarget, string> = {
  customer: 'Clientes',
  contact: 'Contatos',
  vehicle: 'Veículos',
};

interface DatasetCompareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  datasetId: string | null;
  datasetName?: string;
  fields: ExternalDatasetField[];
}

export function DatasetCompareDialog({
  open,
  onOpenChange,
  datasetId,
  datasetName,
  fields,
}: DatasetCompareDialogProps) {
  const [target, setTarget] = useState<CompareTarget>('customer');
  const [matchKey, setMatchKey] = useState<CompareKey>('document');
  const [matchPath, setMatchPath] = useState<string>('');
  const [mode, setMode] = useState<'MATCH' | 'NOT_MATCH'>('NOT_MATCH');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  // Chaves válidas para a entidade escolhida (espelha a whitelist do backend)
  const keyOptions = COMPARE_KEYS_BY_TARGET[target];

  const { rows, totalCount, totalMatch, totalNotMatch, isLoading, isFetching, error } =
    useDatasetComparison({
      datasetId,
      target,
      matchPath: matchPath || null,
      matchKey,
      mode,
      search,
      page,
      pageSize: PAGE_SIZE,
      enabled: open,
    });

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  // Campos oferecidos para o cruzamento — indexados primeiro (mais rápidos)
  const fieldOptions = useMemo(
    () => [...fields].sort((a, b) => Number(b.is_indexed) - Number(a.is_indexed)),
    [fields],
  );

  /** Troca de entidade reajusta a chave para a primeira válida */
  const handleTargetChange = (value: string) => {
    const next = value as CompareTarget;
    setTarget(next);
    setMatchKey(COMPARE_KEYS_BY_TARGET[next][0].value);
    setPage(0);
  };

  /** Exporta a página atual do resultado em CSV */
  const exportCsv = () => {
    const header = ['registro_externo', 'valor_comparado', 'entidade_encontrada'];
    const lines = rows.map((row) =>
      [row.external_id ?? '', row.match_value ?? '', row.matched_entity_label ?? '']
        .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
        .join(';'),
    );
    const blob = new Blob([[header.join(';'), ...lines].join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `comparacao-${datasetName ?? 'dataset'}-${mode.toLowerCase()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[1100px] w-[95vw] h-[85vh] p-0 gap-0 flex flex-col overflow-hidden">
        <PremiumDialogHeader
          icon={GitCompareArrows}
          title="Comparar com a base interna"
          description={datasetName ? `Dataset: ${datasetName}` : "Cruzamento de dados externos"}
        />

        <div className="flex-1 min-h-0 flex flex-col">
          {/* Configuração do cruzamento */}
          <div className="border-b p-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Comparar com</Label>
              <Select value={target} onValueChange={handleTargetChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(TARGET_LABELS) as CompareTarget[]).map((key) => (
                    <SelectItem key={key} value={key}>
                      {TARGET_LABELS[key]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Chave interna</Label>
              <Select
                value={matchKey}
                onValueChange={(v) => {
                  setMatchKey(v as CompareKey);
                  setPage(0);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {keyOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Campo do dataset</Label>
              <Select
                value={matchPath}
                onValueChange={(v) => {
                  setMatchPath(v);
                  setPage(0);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o campo" />
                </SelectTrigger>
                <SelectContent>
                  {fieldOptions.map((field) => (
                    <SelectItem key={field.id} value={field.json_path}>
                      {field.label || field.json_path}
                      {field.is_indexed ? ' ⚡' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Resultado</Label>
              <Select
                value={mode}
                onValueChange={(v) => {
                  setMode(v as 'MATCH' | 'NOT_MATCH');
                  setPage(0);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NOT_MATCH">Não cadastrados</SelectItem>
                  <SelectItem value="MATCH">Já cadastrados</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Totais + busca */}
          <div className="flex flex-wrap items-center gap-2 border-b p-3">
            <Badge variant="secondary" className="gap-1">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
              Já cadastrados: {totalMatch.toLocaleString('pt-BR')}
            </Badge>
            <Badge variant="secondary" className="gap-1">
              <XCircle className="h-3.5 w-3.5 text-amber-600" />
              Não cadastrados: {totalNotMatch.toLocaleString('pt-BR')}
            </Badge>

            <div className="relative ml-auto w-full sm:w-[260px]">
              <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                maxLength={100}
                placeholder="Buscar no registro..."
                className="pl-8"
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
              />
            </div>

            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              disabled={rows.length === 0}
              onClick={exportCsv}
            >
              <Download className="h-4 w-4" />
              Exportar
            </Button>
          </div>

          {/* Resultado */}
          <div className="flex-1 min-h-0 overflow-auto p-3">
            {error && (
              <InlineNotice variant="error" className="mb-3">
                {error.message}
              </InlineNotice>
            )}

            {!matchPath ? (
              <InlineNotice variant="info">
                Selecione o campo do dataset que será cruzado com a base interna.
              </InlineNotice>
            ) : isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 6 }).map((_, index) => (
                  <Skeleton key={index} className="h-10 w-full" />
                ))}
              </div>
            ) : rows.length === 0 ? (
              <div className="py-16 text-center text-sm text-muted-foreground">
                Nenhum registro encontrado para esta comparação.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Registro externo</TableHead>
                    <TableHead>Valor comparado</TableHead>
                    <TableHead>Entidade encontrada</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.record_id}>
                      <TableCell className="font-mono text-xs">
                        {row.external_id || row.record_id.slice(0, 8).toUpperCase()}
                      </TableCell>
                      <TableCell className="text-sm">{row.match_value || '—'}</TableCell>
                      <TableCell className="text-sm">
                        {row.matched_entity_label ? (
                          <span className="text-emerald-600">{row.matched_entity_label}</span>
                        ) : (
                          <span className="text-muted-foreground">Não cadastrado</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          {/* Paginação */}
          <div className="flex items-center justify-between border-t p-3 text-xs text-muted-foreground">
            <span>
              {totalCount.toLocaleString('pt-BR')} registro(s)
              {isFetching ? ' · atualizando...' : ''}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((prev) => Math.max(0, prev - 1))}
              >
                Anterior
              </Button>
              <span>
                {page + 1} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((prev) => prev + 1)}
              >
                Próxima
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
